/**
 * EditorLivePreview — Renders a real-time preview of the selected step
 * in the work area. Manipulates GameScene camera and draws overlay
 * elements (bubble preview, cursor marker, target crosshair).
 *
 * Listens to: STEP_SELECTED, STEP_UPDATED, CAPTURE_DONE
 */
import Phaser from 'phaser';
import { WORK_AREA, EditorEvents } from './EditorTypes';
import type { EditorState } from './EditorTypes';
import { TECH } from '@/ui/TechUI';
import { EventBus } from '@/events/EventBus';
import { CameraDirector } from '../CameraDirector';
import { t } from '@/i18n/I18n';
import { EditorBubbleDrag } from './EditorBubbleDrag';
import { RecapPanel } from '../RecapPanel';
import type { DialogueStep } from '../CinematicTypes';

const EMOTION_COLORS: Record<string, number> = {
  alert: 0xff4444, calm: TECH.ACCENT, excited: TECH.SUCCESS,
};

export class EditorLivePreview {
  private scene: Phaser.Scene;
  private state: EditorState;
  private container: Phaser.GameObjects.Container;
  private cameraDir: CameraDirector | null = null;

  // Saved camera state to restore when deselecting
  private savedCam = { x: 0, y: 0, zoom: 1 };
  private hasSavedCam = false;

  // Host scene resume state for camera preview
  private hostResumedForPreview = false;

  // Interactive BD bubble drag for dialogue steps
  private bubbleDrag!: EditorBubbleDrag;

  // Interactive camera manipulation for cameraPan steps
  private camDragActive = false;
  private camDragZone: Phaser.GameObjects.Rectangle | null = null;
  private camWheelHandler: ((p: any, gx: any, dx: number, dy: number) => void) | null = null;
  private camMoveHandler: ((p: Phaser.Input.Pointer) => void) | null = null;
  private camUpHandler: (() => void) | null = null;
  private camUpdateHandler: (() => void) | null = null;
  private suppressUpdate = false; // prevent re-render loop during interactive cam manipulation
  private camEmitDirty = false;   // throttled STEP_UPDATED during continuous cam manipulation
  private recapPanel: RecapPanel | null = null; // reuse real RecapPanel for WYSIWYG preview
  private camEmitTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly CAM_EMIT_INTERVAL = 80; // ms between throttled emissions

  constructor(scene: Phaser.Scene, state: EditorState) {
    this.scene = scene;
    this.state = state;
    this.container = scene.add.container(0, 0).setDepth(5);
    this.bubbleDrag = new EditorBubbleDrag(scene, this.container);

    if (state.hostScene) {
      this.cameraDir = new CameraDirector(state.hostScene);
    }

    EventBus.on(EditorEvents.STEP_SELECTED, this.onStepSelected, this);
    EventBus.on(EditorEvents.STEP_ADDED, this.onStepSelected, this);
    EventBus.on(EditorEvents.STEP_UPDATED, this.onUpdate, this);
    EventBus.on(EditorEvents.CAPTURE_DONE, this.onUpdate, this);
    EventBus.on(EditorEvents.STEP_REMOVED, this.onUpdate, this);
    EventBus.on(EditorEvents.STEPS_REORDERED, this.onUpdate, this);
  }

  /** Step selection always forces a full cleanup + re-render (prevents stale tail handles) */
  private onStepSelected(): void {
    if (this.state.mode !== 'edit') return;
    this.renderPreview();
  }

  private onUpdate(): void {
    if (this.state.mode !== 'edit') return;
    if (this.suppressUpdate || this.bubbleDrag.isSuppressing) return;
    this.renderPreview();
  }

  /** Clear and redraw all preview elements for the selected step */
  renderPreview(): void {
    this.cleanupCamInteraction();
    this.cleanupRecapPanel();
    this.bubbleDrag.cleanup();
    this.container.removeAll(true);

    const idx = this.state.selectedStepIndex;
    if (idx < 0 || idx >= this.state.sequence.steps.length) {
      this.restoreCamera();
      this.renderEmptyState();
      return;
    }

    const step = this.state.sequence.steps[idx];

    // For non-cameraPan steps, apply the camera state from the last cameraPan before this step
    if (step.type !== 'cameraPan') {
      this.applyCameraContext(idx);
    }

    switch (step.type) {
      case 'cameraPan':  this.previewCameraPan(step as any); break;
      case 'dialogue':   this.previewDialogue(step as any); break;
      case 'fakeClick':  this.previewFakeClick(step as any); break;
      case 'action':     this.previewAction(step as any); break;
      case 'wait':       this.previewWait(); break;
      case 'recap':      this.previewRecap(step as any); break;
      case 'spotlight':  this.previewSpotlight(step as any); break;
    }
  }

  /** Apply camera position from the last cameraPan step before the given index.
   *  Uses viewport center (not work-area center) so bubble/spotlight positions
   *  match between editor preview and runtime playback (WYSIWYG). */
  private applyCameraContext(beforeIndex: number): void {
    const gs = this.state.hostScene;
    if (!gs || !this.cameraDir) return;

    // Walk backwards to find the last cameraPan
    let foundCamStep: { target: any; zoom?: number } | null = null;
    for (let i = beforeIndex - 1; i >= 0; i--) {
      const s = this.state.sequence.steps[i];
      if (s.type === 'cameraPan') {
        foundCamStep = s as { target: any; zoom?: number };
        break;
      }
    }

    // Resume host scene so camera renders (even without a previous cameraPan)
    if (!this.hostResumedForPreview) {
      this.scene.scene.resume(this.state.hostSceneKey);
      this.scene.registry.set('tutorialCameraLock', true);
      this.hostResumedForPreview = true;
    }
    // Save camera once
    if (!this.hasSavedCam) {
      const cam = gs.cameras.main;
      this.savedCam = { x: cam.scrollX, y: cam.scrollY, zoom: cam.zoom };
      this.hasSavedCam = true;
    }
    const cam = gs.cameras.main;
    cam.stopFollow();
    (cam as any).panEffect?.reset();
    (cam as any).zoomEffect?.reset();
    cam.roundPixels = false;

    if (foundCamStep) {
      const pos = this.cameraDir.resolveTarget(foundCamStep.target);
      const zoom = foundCamStep.zoom ?? 1.5;
      cam.zoom = zoom;
      cam.scrollX = pos.x - cam.width / 2;
      cam.scrollY = pos.y - cam.height / 2;
    }
    // If no previous cameraPan found, keep the current camera position —
    // the host scene is now resumed and will render the map as-is.
  }

  // ── Camera Pan preview (interactive: drag to pan, scroll to zoom) ──
  /** Camera pan preview — uses viewport center for WYSIWYG with runtime. */
  private previewCameraPan(step: { target: any; zoom?: number; duration?: number }): void {
    const gs = this.state.hostScene;
    if (!gs || !this.cameraDir) return;

    // Resume host scene so its camera renders the game world
    if (!this.hostResumedForPreview) {
      this.scene.scene.resume(this.state.hostSceneKey);
      this.scene.registry.set('tutorialCameraLock', true);
      this.hostResumedForPreview = true;
    }

    // Save camera once
    if (!this.hasSavedCam) {
      const cam = gs.cameras.main;
      this.savedCam = { x: cam.scrollX, y: cam.scrollY, zoom: cam.zoom };
      this.hasSavedCam = true;
    }

    // Kill any active camera tweens from GameScene (they survive scene.pause!)
    const cam = gs.cameras.main;
    cam.stopFollow();
    (cam as any).panEffect?.reset();
    (cam as any).zoomEffect?.reset();
    cam.roundPixels = false;

    const halfW = cam.width / 2;
    const halfH = cam.height / 2;

    // Resolve target and position camera — center at viewport center
    // to match runtime cam.pan() behavior (WYSIWYG)
    const pos = this.cameraDir.resolveTarget(step.target);
    const zoom = step.zoom ?? 1.5;
    cam.zoom = zoom;
    cam.scrollX = pos.x - halfW;
    cam.scrollY = pos.y - halfH;

    // Draw crosshair at viewport center (where target appears at runtime)
    this.drawCrosshair(halfW, halfH, 0x44aaff);

    // Info label
    const label = typeof step.target === 'string'
      ? `target: "${step.target}"  zoom: ${zoom}`
      : `target: (${Math.round(pos.x)}, ${Math.round(pos.y)})  zoom: ${zoom}`;
    this.addInfoBadge(label, 0x44aaff);

    // Hint text
    const hint = this.scene.add.text(WORK_AREA.w / 2, WORK_AREA.y + 12, 'Drag / Arrow keys to pan — Scroll to zoom', {
      fontSize: '10px', color: '#44aaff', fontFamily: 'monospace', align: 'center',
    }).setOrigin(0.5, 0).setAlpha(0.7);
    this.container.add(hint);

    // ── Interactive drag zone over work area ──
    this.camDragZone = this.scene.add.rectangle(
      WORK_AREA.x + WORK_AREA.w / 2, WORK_AREA.y + WORK_AREA.h / 2,
      WORK_AREA.w, WORK_AREA.h, 0x000000, 0,
    ).setDepth(6).setInteractive({ useHandCursor: true });

    // Drag to pan — track lastX/Y manually (Phaser's ptr.prevPosition can be stale)
    let lastX = 0;
    let lastY = 0;
    this.camMoveHandler = (ptr: Phaser.Input.Pointer) => {
      if (!this.camDragActive || !ptr.isDown) return;
      const dx = ptr.x - lastX;
      const dy = ptr.y - lastY;
      lastX = ptr.x;
      lastY = ptr.y;
      if (dx === 0 && dy === 0) return;

      cam.scrollX -= dx / cam.zoom;
      cam.scrollY -= dy / cam.zoom;

      // Update step target = world point at viewport center
      const cx = Math.round(cam.scrollX + halfW);
      const cy = Math.round(cam.scrollY + halfH);
      step.target = { x: cx, y: cy };
      this.emitThrottled(EditorEvents.STEP_UPDATED);
    };

    this.camDragZone.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      this.camDragActive = true;
      lastX = ptr.x;
      lastY = ptr.y;
    });
    this.camUpHandler = () => { this.camDragActive = false; };
    this.scene.input.on('pointerup', this.camUpHandler);
    this.scene.input.on('pointermove', this.camMoveHandler);

    // Scroll to zoom — centered on pointer (Google Maps style)
    this.camWheelHandler = (_p: any, _gx: any, _dx: number, dy: number) => {
      const ptr = this.scene.input.activePointer;
      if (ptr.x < WORK_AREA.x || ptr.x > WORK_AREA.x + WORK_AREA.w ||
          ptr.y < WORK_AREA.y || ptr.y > WORK_AREA.y + WORK_AREA.h) return;

      const oldZoom = cam.zoom;
      const newZoom = Phaser.Math.Clamp(oldZoom - dy * 0.002, 0.3, 5);
      // Keep the world point under the pointer fixed (Phaser zoom-from-center)
      const worldX = cam.scrollX + halfW + (ptr.x - halfW) / oldZoom;
      const worldY = cam.scrollY + halfH + (ptr.y - halfH) / oldZoom;
      cam.zoom = newZoom;
      cam.scrollX = worldX - halfW - (ptr.x - halfW) / newZoom;
      cam.scrollY = worldY - halfH - (ptr.y - halfH) / newZoom;

      // Update step target = world point at viewport center
      step.zoom = Math.round(newZoom * 100) / 100;
      step.target = {
        x: Math.round(cam.scrollX + halfW),
        y: Math.round(cam.scrollY + halfH),
      };
      this.emitThrottled(EditorEvents.STEP_UPDATED);
    };
    this.scene.input.on('wheel', this.camWheelHandler);

    // Arrow keys to pan (continuous while held)
    const CAM_KEY_SPEED = 4;
    const kb = this.scene.input.keyboard;
    if (kb) {
      this.camUpdateHandler = () => {
        let moved = false;
        if (kb.addKey('LEFT').isDown || kb.addKey('A').isDown)  { cam.scrollX -= CAM_KEY_SPEED / cam.zoom; moved = true; }
        if (kb.addKey('RIGHT').isDown || kb.addKey('D').isDown) { cam.scrollX += CAM_KEY_SPEED / cam.zoom; moved = true; }
        if (kb.addKey('UP').isDown || kb.addKey('W').isDown)    { cam.scrollY -= CAM_KEY_SPEED / cam.zoom; moved = true; }
        if (kb.addKey('DOWN').isDown || kb.addKey('S').isDown)  { cam.scrollY += CAM_KEY_SPEED / cam.zoom; moved = true; }
        if (moved) {
          const cx = Math.round(cam.scrollX + halfW);
          const cy = Math.round(cam.scrollY + halfH);
          step.target = { x: cx, y: cy };
          this.emitThrottled(EditorEvents.STEP_UPDATED);
        }
      };
      this.scene.events.on('update', this.camUpdateHandler);
    }
  }

  /** Remove interactive camera handlers */
  private cleanupCamInteraction(): void {
    if (this.camEmitTimer) {
      clearTimeout(this.camEmitTimer);
      this.camEmitTimer = null;
    }
    // Flush any pending dirty state so the panel gets the final value
    if (this.camEmitDirty) {
      this.camEmitDirty = false;
      this.emitSuppressed(EditorEvents.STEP_UPDATED);
    }
    if (this.camMoveHandler) {
      this.scene.input.off('pointermove', this.camMoveHandler);
      this.camMoveHandler = null;
    }
    if (this.camWheelHandler) {
      this.scene.input.off('wheel', this.camWheelHandler);
      this.camWheelHandler = null;
    }
    if (this.camUpdateHandler) {
      this.scene.events.off('update', this.camUpdateHandler);
      this.camUpdateHandler = null;
    }
    if (this.camUpHandler) {
      this.scene.input.off('pointerup', this.camUpHandler);
      this.camUpHandler = null;
    }
    if (this.camDragZone) {
      this.camDragZone.destroy();
      this.camDragZone = null;
    }
    this.camDragActive = false;

    // Re-pause host scene if we resumed it for camera preview
    if (this.hostResumedForPreview) {
      this.scene.registry.set('tutorialCameraLock', false);
      if (this.scene.scene.isActive(this.state.hostSceneKey)) {
        this.scene.scene.pause(this.state.hostSceneKey);
      }
      this.hostResumedForPreview = false;
    }
  }

  // ── Dialogue preview (interactive: drag bubble + tail) ──
  private previewDialogue(step: DialogueStep): void {
    // Draw highlight rect if present (behind the bubble)
    if (step.highlightTarget) {
      this.drawHighlightPreview(
        step.highlightTarget,
        !!step.highlightScreenSpace,
        step.highlightDimAlpha ?? 0.55,
        step.highlightColor ?? TECH.ACCENT,
        step.highlightPulse ?? true,
      );
    }

    this.bubbleDrag.render(step, this.state.hostScene ?? null);
    const emotion = step.emotion ?? 'calm';
    const borderColor = EMOTION_COLORS[emotion] ?? TECH.ACCENT;
    const tmplId = step.bubbleTemplate ?? 'trueBD';
    this.addInfoBadge(`template: ${tmplId}  position: ${step.position ?? 'bottom'}  emotion: ${emotion}`, borderColor);
  }

  // ── Fake Click preview ──
  private previewFakeClick(step: { target: any; screenSpace?: boolean; holdMs?: number }): void {
    const gs = this.state.hostScene;
    if (!gs) return;

    let sx: number, sy: number;
    let label: string;
    const spaceLabel = step.screenSpace ? '(screen)' : '(world)';

    if (typeof step.target === 'string') {
      // Named target — show in center with label
      sx = WORK_AREA.w / 2;
      sy = WORK_AREA.y + WORK_AREA.h / 2;
      label = `target: "${step.target}"`;
    } else if (step.screenSpace) {
      // Screen space — use coords directly
      sx = step.target.x;
      sy = step.target.y;
      label = `target ${spaceLabel}: (${step.target.x}, ${step.target.y})`;
    } else {
      // World space — project to screen via camera
      const cam = gs.cameras.main;
      const halfW = cam.width / 2;
      const halfH = cam.height / 2;
      sx = (step.target.x - cam.scrollX - halfW) * cam.zoom + halfW;
      sy = (step.target.y - cam.scrollY - halfH) * cam.zoom + halfH;
      label = `target ${spaceLabel}: (${step.target.x}, ${step.target.y})`;
    }

    // Cursor icon (white arrow)
    const gfx = this.scene.add.graphics();
    gfx.fillStyle(0xffffff, 0.9);
    gfx.fillTriangle(sx, sy, sx + 12, sy + 16, sx + 16, sy + 8);
    gfx.lineStyle(1, 0x000000, 0.5);
    gfx.strokeTriangle(sx, sy, sx + 12, sy + 16, sx + 16, sy + 8);
    this.container.add(gfx);

    // Click ring
    const ring = this.scene.add.graphics();
    ring.lineStyle(2, 0xff8844, 0.6);
    ring.strokeCircle(sx, sy, 18);
    ring.lineStyle(1, 0xff8844, 0.3);
    ring.strokeCircle(sx, sy, 28);
    this.container.add(ring);

    this.addInfoBadge(`${label}  hold: ${step.holdMs ?? 500}ms`, 0xff8844);
  }

  // ── Action preview ──
  private previewAction(step: { action: string; params?: any }): void {
    const txt = this.scene.add.text(WORK_AREA.w / 2, WORK_AREA.y + WORK_AREA.h / 2, `⚡ ${step.action}`, {
      fontSize: '18px', color: '#44ff88', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.container.add(txt);

    const params = JSON.stringify(step.params ?? {});
    const paramTxt = this.scene.add.text(WORK_AREA.w / 2, WORK_AREA.y + WORK_AREA.h / 2 + 30, params, {
      fontSize: '11px', color: '#88aa88', fontFamily: 'monospace',
    }).setOrigin(0.5);
    this.container.add(paramTxt);

    this.addInfoBadge(`action: ${step.action}`, 0x44ff88);
  }

  // ── Wait preview ──
  private previewWait(): void {
    const txt = this.scene.add.text(WORK_AREA.w / 2, WORK_AREA.y + WORK_AREA.h / 2, '⏳ WAIT', {
      fontSize: '18px', color: '#888899', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.container.add(txt);
  }

  // ── Recap preview (uses real RecapPanel for WYSIWYG) ──
  private previewRecap(step: { titleKey: string; items?: any[]; confirmKey: string }): void {
    this.cleanupRecapPanel();
    this.recapPanel = new RecapPanel(this.scene);
    this.recapPanel.show({
      titleKey: step.titleKey,
      items: step.items ?? [],
      confirmKey: step.confirmKey,
    });
    this.addInfoBadge(`recap: ${(step.items ?? []).length} items`, 0xaa88ff);
  }

  // ── Spotlight preview ──
  private previewSpotlight(step: {
    target: string | { x: number; y: number; w: number; h: number } | Array<{ x: number; y: number; w: number; h: number }>;
    screenSpace?: boolean;
    dimAlpha?: number; highlightColor?: number; pulse?: boolean;
    labelKey?: string; labelPosition?: string; arrowDir?: string;
    advanceOn?: string; restrictInput?: boolean;
  }): void {
    const cx = WORK_AREA.w / 2;
    const cy = WORK_AREA.y + WORK_AREA.h / 2;
    const gfx = this.scene.add.graphics();
    const dimAlpha = step.dimAlpha ?? 0.55;
    const highlightColor = step.highlightColor ?? TECH.ACCENT;
    const pad = 6;

    // Convert world rect to screen rect using host scene camera
    const toScreen = (r: { x: number; y: number; w: number; h: number }) => {
      if (step.screenSpace) return r; // already in screen coords
      const cam = this.state.hostScene?.cameras.main;
      if (!cam) return r;
      const halfW = cam.width / 2;
      const halfH = cam.height / 2;
      const sx = (r.x - cam.scrollX - halfW) * cam.zoom + halfW;
      const sy = (r.y - cam.scrollY - halfH) * cam.zoom + halfH;
      return { x: sx, y: sy, w: r.w * cam.zoom, h: r.h * cam.zoom };
    };

    if (typeof step.target === 'string') {
      // Named target — show dim overlay with centered placeholder
      gfx.fillStyle(0x000000, dimAlpha * 0.6);
      gfx.fillRect(WORK_AREA.x, WORK_AREA.y, WORK_AREA.w, WORK_AREA.h);
      this.container.add(gfx);

      const label = this.scene.add.text(cx, cy, `🔦 (target: ${step.target})`, {
        fontSize: '16px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5);
      this.container.add(label);

      const hint = this.scene.add.text(cx, cy + 30, 'Named target — resolved at runtime', {
        fontSize: '10px', color: '#888899', fontFamily: 'monospace',
      }).setOrigin(0.5);
      this.container.add(hint);
    } else {
      // Explicit rect(s) — convert to screen space for preview, then draw
      const rawRects = Array.isArray(step.target) ? step.target : [step.target];
      const rects = rawRects.map(toScreen);

      // Dim overlay
      gfx.fillStyle(0x000000, dimAlpha * 0.6);
      gfx.fillRect(WORK_AREA.x, WORK_AREA.y, WORK_AREA.w, WORK_AREA.h);
      // ERASE cutouts
      gfx.fillStyle(0x000000, 0);
      gfx.setBlendMode(Phaser.BlendModes.ERASE);
      for (const r of rects) {
        gfx.fillRoundedRect(r.x - pad, r.y - pad, r.w + pad * 2, r.h + pad * 2, 6);
      }
      gfx.setBlendMode(Phaser.BlendModes.NORMAL);
      this.container.add(gfx);

      // Highlight borders
      const hlGfx = this.scene.add.graphics();
      hlGfx.lineStyle(2.5, highlightColor, 0.9);
      for (const r of rects) {
        hlGfx.strokeRoundedRect(r.x - pad, r.y - pad, r.w + pad * 2, r.h + pad * 2, 6);
      }
      this.container.add(hlGfx);

      // Pulse animation
      if (step.pulse !== false) {
        this.scene.tweens.add({
          targets: hlGfx,
          alpha: { from: 1, to: 0.3 },
          duration: 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        });
      }

      // Centroid + bounding box for label/arrow positioning
      let tcx = 0, tcy = 0;
      let minY = Infinity, maxY = -Infinity;
      for (const r of rects) { tcx += r.x + r.w / 2; tcy += r.y + r.h / 2; minY = Math.min(minY, r.y); maxY = Math.max(maxY, r.y + r.h); }
      tcx /= rects.length; tcy /= rects.length;

      // Label
      if (step.labelKey) {
        const labelStr = t(step.labelKey);
        const labelPos = step.labelPosition ?? 'bottom';
        let ly: number, originY: number;
        if (labelPos === 'top') { ly = minY - pad - 6; originY = 1; }
        else { ly = maxY + pad + 6; originY = 0; }
        const labelTxt = this.scene.add.text(tcx, ly, labelStr, {
          fontSize: '11px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
        }).setOrigin(0.5, originY);
        this.container.add(labelTxt);
      }
    }

    const targetStr = typeof step.target === 'string'
      ? `target: "${step.target}"`
      : Array.isArray(step.target)
        ? `rects: ${step.target.length}`
        : `rect: (${step.target.x},${step.target.y},${step.target.w},${step.target.h})`;
    const advStr = step.advanceOn ?? 'click';
    this.addInfoBadge(`${targetStr}  advance: ${advStr}  dim: ${dimAlpha}`, 0xff44cc);
  }

  // ── Shared highlight drawing (used by dialogue + spotlight previews) ──

  private drawHighlightPreview(
    target: string | { x: number; y: number; w: number; h: number } | Array<{ x: number; y: number; w: number; h: number }>,
    screenSpace: boolean,
    dimAlpha: number,
    highlightColor: number,
    pulse: boolean,
  ): void {
    const pad = 6;
    const gfx = this.scene.add.graphics();

    const toScreen = (r: { x: number; y: number; w: number; h: number }) => {
      if (screenSpace) return r;
      const cam = this.state.hostScene?.cameras.main;
      if (!cam) return r;
      const halfW = cam.width / 2;
      const halfH = cam.height / 2;
      return {
        x: (r.x - cam.scrollX - halfW) * cam.zoom + halfW,
        y: (r.y - cam.scrollY - halfH) * cam.zoom + halfH,
        w: r.w * cam.zoom,
        h: r.h * cam.zoom,
      };
    };

    if (typeof target === 'string') {
      // Named target — dim overlay with label
      gfx.fillStyle(0x000000, dimAlpha * 0.6);
      gfx.fillRect(WORK_AREA.x, WORK_AREA.y, WORK_AREA.w, WORK_AREA.h);
      this.container.add(gfx);
      const label = this.scene.add.text(
        WORK_AREA.x + WORK_AREA.w / 2, WORK_AREA.y + 20,
        `🔦 highlight: ${target}`, {
          fontSize: '10px', color: '#ffcc00', fontFamily: 'monospace',
        },
      ).setOrigin(0.5, 0);
      this.container.add(label);
    } else {
      // Explicit rect(s)
      const rawRects = Array.isArray(target) ? target : [target];
      const rects = rawRects.map(toScreen);

      gfx.fillStyle(0x000000, dimAlpha * 0.6);
      gfx.fillRect(WORK_AREA.x, WORK_AREA.y, WORK_AREA.w, WORK_AREA.h);
      gfx.fillStyle(0x000000, 0);
      gfx.setBlendMode(Phaser.BlendModes.ERASE);
      for (const r of rects) {
        gfx.fillRoundedRect(r.x - pad, r.y - pad, r.w + pad * 2, r.h + pad * 2, 6);
      }
      gfx.setBlendMode(Phaser.BlendModes.NORMAL);
      this.container.add(gfx);

      const hlGfx = this.scene.add.graphics();
      hlGfx.lineStyle(2.5, highlightColor, 0.9);
      for (const r of rects) {
        hlGfx.strokeRoundedRect(r.x - pad, r.y - pad, r.w + pad * 2, r.h + pad * 2, 6);
      }
      this.container.add(hlGfx);

      if (pulse) {
        this.scene.tweens.add({
          targets: hlGfx,
          alpha: { from: 1, to: 0.3 },
          duration: 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        });
      }
    }
  }

  // ── Empty state (no step selected) ──

  private renderEmptyState(): void {
    const { x, y, w, h } = WORK_AREA;
    const gfx = this.scene.add.graphics();

    // Animated halo border — soft inner glow
    const glowColor = 0x4466aa;
    const layers = 4;
    for (let i = layers; i >= 1; i--) {
      const alpha = 0.06 + (layers - i) * 0.04; // 0.06 → 0.18
      const inset = i * 3;
      gfx.lineStyle(2, glowColor, alpha);
      gfx.strokeRect(x + inset, y + inset, w - inset * 2, h - inset * 2);
    }
    // Bright edge
    gfx.lineStyle(1.5, glowColor, 0.35);
    gfx.strokeRect(x, y, w, h);

    // Corner accents (small L-shapes)
    const cornerLen = 20;
    const ca = 0.5;
    gfx.lineStyle(2, TECH.ACCENT, ca);
    // Top-left
    gfx.lineBetween(x, y, x + cornerLen, y);
    gfx.lineBetween(x, y, x, y + cornerLen);
    // Top-right
    gfx.lineBetween(x + w, y, x + w - cornerLen, y);
    gfx.lineBetween(x + w, y, x + w, y + cornerLen);
    // Bottom-left
    gfx.lineBetween(x, y + h, x + cornerLen, y + h);
    gfx.lineBetween(x, y + h, x, y + h - cornerLen);
    // Bottom-right
    gfx.lineBetween(x + w, y + h, x + w - cornerLen, y + h);
    gfx.lineBetween(x + w, y + h, x + w, y + h - cornerLen);

    this.container.add(gfx);

    // Central icon
    const cx = x + w / 2;
    const cy = y + h / 2 - 20;
    const icon = this.scene.add.text(cx, cy, '◇', {
      fontSize: '32px', color: '#4466aa', fontFamily: 'monospace',
    }).setOrigin(0.5).setAlpha(0.5);
    this.container.add(icon);

    // Instruction text
    const hasSteps = this.state.sequence.steps.length > 0;
    const msg = hasSteps
      ? 'Select a step in the timeline\nto preview it here'
      : 'Add a cinematic step with +ADD\nto start building your sequence';
    const txt = this.scene.add.text(cx, cy + 30, msg, {
      fontSize: '12px', color: '#6677aa', fontFamily: 'monospace',
      align: 'center', lineSpacing: 6,
    }).setOrigin(0.5, 0);
    this.container.add(txt);

    // Subtle pulsing tween on the icon
    this.scene.tweens.add({
      targets: icon, alpha: 0.2, duration: 1500,
      yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
  }

  /** Emit an event without triggering our own onUpdate (prevents re-render loop) */
  private emitSuppressed(event: string): void {
    this.suppressUpdate = true;
    EventBus.emit(event);
    this.suppressUpdate = false;
  }

  /** Throttled emit — marks dirty and flushes at most every CAM_EMIT_INTERVAL ms */
  private emitThrottled(event: string): void {
    this.camEmitDirty = true;
    if (this.camEmitTimer) return; // timer already scheduled
    this.camEmitTimer = setTimeout(() => {
      this.camEmitTimer = null;
      if (this.camEmitDirty) {
        this.camEmitDirty = false;
        this.emitSuppressed(event);
      }
    }, EditorLivePreview.CAM_EMIT_INTERVAL);
  }

  // ── Helpers ──

  private drawCrosshair(x: number, y: number, color: number, size = 14): void {
    const gfx = this.scene.add.graphics();
    gfx.lineStyle(2, color, 0.8);
    gfx.lineBetween(x - size, y, x + size, y);
    gfx.lineBetween(x, y - size, x, y + size);
    gfx.lineStyle(1, color, 0.4);
    gfx.strokeCircle(x, y, size);
    this.container.add(gfx);
  }

  private addInfoBadge(text: string, color: number): void {
    const bx = 10;
    const by = WORK_AREA.y + WORK_AREA.h - 30;
    const gfx = this.scene.add.graphics();
    gfx.fillStyle(0x000000, 0.7);
    gfx.fillRoundedRect(bx, by, text.length * 7 + 16, 22, 4);
    gfx.lineStyle(1, color, 0.5);
    gfx.strokeRoundedRect(bx, by, text.length * 7 + 16, 22, 4);
    this.container.add(gfx);

    const txt = this.scene.add.text(bx + 8, by + 4, text, {
      fontSize: '10px', color: `#${color.toString(16).padStart(6, '0')}`, fontFamily: 'monospace',
    });
    this.container.add(txt);
  }

  private restoreCamera(): void {
    if (!this.hasSavedCam || !this.state.hostScene) return;
    const cam = this.state.hostScene.cameras.main;
    cam.scrollX = this.savedCam.x;
    cam.scrollY = this.savedCam.y;
    cam.zoom = this.savedCam.zoom;
    cam.roundPixels = true; // restore (was disabled during interactive editing)
    this.hasSavedCam = false;
  }

  /** Tear down all preview objects and handlers without destroying the component */
  clearPreview(): void {
    this.cleanupCamInteraction();
    this.cleanupRecapPanel();
    this.bubbleDrag.cleanup();
    this.container.removeAll(true);
  }

  setVisible(visible: boolean): void { this.container.setVisible(visible); }

  private cleanupRecapPanel(): void {
    if (this.recapPanel) {
      this.recapPanel.hide(true); // silent — don't emit RECAP_CLOSED
      this.recapPanel = null;
    }
  }

  destroy(): void {
    this.cleanupCamInteraction();
    this.cleanupRecapPanel();
    this.bubbleDrag.destroy();
    EventBus.off(EditorEvents.STEP_SELECTED, this.onStepSelected, this);
    EventBus.off(EditorEvents.STEP_ADDED, this.onStepSelected, this);
    EventBus.off(EditorEvents.STEP_UPDATED, this.onUpdate, this);
    EventBus.off(EditorEvents.CAPTURE_DONE, this.onUpdate, this);
    EventBus.off(EditorEvents.STEP_REMOVED, this.onUpdate, this);
    EventBus.off(EditorEvents.STEPS_REORDERED, this.onUpdate, this);
    this.restoreCamera();
    this.container.destroy();
  }
}
